import { binToMeasurement } from 'features/fde/binningUtils'
import {
  GeckoComponentDataFrame,
  GeckoEntry,
  GeckoMarkupLayer,
  GeckoMeasurementUnit,
  RolodexEntryJson,
  RolodexEntryType,
} from 'types'

const STD_UNIT = GeckoMeasurementUnit.Millimeters
const CTP_LINE_COLOR = '#1890ff'

/**
 * Prefix applied to every markup-layer id this module emits. Use the helper
 * below rather than reading the constant directly so consumers (e.g. the viz
 * layer-visibility control) stay in lockstep with what this module produces.
 */
export const FFS_CTP_LAYER_PREFIX = 'ffs-triage-ctp_'

/** True iff `id` is a markup-layer id produced by `ctpEntryToLayer`. */
export const isCtpLayerId = (id: string): boolean =>
  id.startsWith(FFS_CTP_LAYER_PREFIX)

/**
 * Converts a single `FFS_CRITICAL_THICKNESS_PROFILE` Rolodex entry into a
 * line-primitive markup layer.
 *
 * Ports the private converter from `criticalProfileMarkup.ts:52-83` and the
 * bin -> measurement conversion from `criticalProfileApi.ts:105-141`.
 *
 * Markup layer shape mirrors `criticalProfileMarkup.ts:64-83`:
 *   data.primitives is a Record<string, Primitive> MAP (NOT an array), with
 *   one entry `crit_prof: { kind: 'line', points: [{x, y}, ...] }`.
 *
 * Returns `[]` if the CDF can't be resolved or there are fewer than 2 bins.
 */
export function ctpEntryToLayer(
  entry: GeckoEntry,
  cdf: GeckoComponentDataFrame | undefined,
  /** 1-based position, used to synthesize a readable name when untitled. */
  ordinal?: number
): GeckoMarkupLayer[] {
  if (!cdf) return []
  const bins = (entry.data as { critical_path_bins?: [number, number][] })
    .critical_path_bins
  if (!bins || bins.length < 2) return []

  try {
    // CDF reads can throw on malformed / partial CDFs (missing
    // bin_size / geometry). Keep them inside the try so a bad CDF
    // skips this CTP rather than crashing the triage step.
    const binWidth = cdf.data.binning.bin_size.width.convertTo(STD_UNIT).num
    const binHeight = cdf.data.binning.bin_size.height.convertTo(STD_UNIT).num
    const geomStartX = cdf.data.geometry.start.x.convertTo(STD_UNIT).num
    const geomStartY = cdf.data.geometry.start.y.convertTo(STD_UNIT).num

    const points = bins.map(([binX, binY]) => ({
      x: binToMeasurement(binX, geomStartX, binWidth),
      y: binToMeasurement(binY, geomStartY, binHeight),
    }))

    const geometry = {
      x_measurement_unit: STD_UNIT.id,
      y_measurement_unit: STD_UNIT.id,
    }

    const primitives: Record<string, unknown> = {
      crit_prof: {
        kind: 'line',
        points,
      },
    }

    const json: RolodexEntryJson = {
      id: `${FFS_CTP_LAYER_PREFIX}${entry.id}`,
      type: RolodexEntryType.MARKUP_LAYER,
      tags: entry.tags,
      data: {
        name:
          (entry.data as { title?: string }).title ??
          (ordinal !== undefined ? `CTP ${ordinal}` : entry.id),
        path: ['FFS LTA Paths'],
        customer_visible: true,
        color: CTP_LINE_COLOR,
        category: 'user-added',
        geometry,
        primitives,
      },
      index: {},
      generation: 1,
      updated_at: new Date(0).toISOString(),
      access_restriction: null,
    }

    return [GeckoMarkupLayer.CreateFromJSON(json)]
  } catch (err) {
    console.warn(`Failed to construct CTP markup layer ${entry.id}:`, err)
    return []
  }
}
